'use client';
import { Image } from '@repo/ui/Image';
import clsx from 'clsx';
import { FC, useRef, useState } from 'react';

import { Icon } from '@/general/Icons/Icon';
import { Button } from '@/general/components/Button/Button';
import { formatNumber } from '@/general/helpers/formatNumber';
import { isEmpty } from '@/utils/isEmpty';
import { pushToDataLayer } from '@/utils/pushToDataLayer';

import { ServiceHistoryRecord } from './ServiceHistory.type';

type Props = {
  serviceHistory?: ServiceHistoryRecord[];
};

const HEADER_OFFSET = 130;

export const ServiceHistoryDetail: FC<Props> = ({ serviceHistory }) => {
  const [isShowList, setIsShowList] = useState(false);
  const latestItem = serviceHistory?.[0] ?? null;
  const hasMultipleServices = serviceHistory && serviceHistory.length > 1;

  const containerRef = useRef<null | HTMLHeadingElement>(null);

  const toggleShowList = () => {
    if (!isShowList && containerRef.current) {
      const accordionPosition = containerRef.current?.getBoundingClientRect().top;
      window.scrollTo({
        top: accordionPosition + window.scrollY - HEADER_OFFSET,
        behavior: 'smooth',
      });
    }
    setIsShowList((isShowList) => !isShowList);
    pushToDataLayer({
      event: 'pdp_interaction',
      type: 'accordion',
      value: 'view_service_history',
    });
  };

  return (
    <div className="rounded-lg bg-bac-tie2 p-4" ref={containerRef}>
      <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
        <div className="flex w-full gap-2 md:gap-4">
          <Image
            src="/images/components/BadgeIllustration/carma-touch.svg"
            alt="Carma Touch"
            width={56}
            height={56}
            className="size-14"
          />

          <div className="c-fs-body2 self-center">
            {latestItem ? (
              <>
                <p>{hasMultipleServices ? 'Last service' : 'This car has only had one service'}</p>
                <p className="font-extrabold">
                  <span data-testid="ServiceHistory-latestServiceDate">{formatServiceDate(latestItem)}</span> •{' '}
                  <span data-testid="ServiceHistory-latestKilometres">{formatServiceKilometres(latestItem)}</span>
                </p>
              </>
            ) : (
              <p className="c-fs-body2 font-normal" data-testid="empty-service-history">
                This car is relatively new and still hasn’t had its first service, or we may not have access to it’s
                service history.
              </p>
            )}
          </div>
        </div>
        {hasMultipleServices && (
          <div className="w-full sm:w-auto">
            <Button
              data-testid="ServiceHistory-expand"
              variant="tertiary"
              size="small"
              endIcon={isShowList ? <Icon name="caret-up" /> : <Icon name="caret-down" />}
              onClick={toggleShowList}
              fullWidth
            >
              {isShowList ? 'Hide service history' : 'View service history'}
            </Button>
          </div>
        )}
      </div>

      {hasMultipleServices && (
        <ul
          className={clsx('list-none', {
            ['hidden']: !isShowList,
          })}
        >
          {serviceHistory.map((item, index) => (
            <li
              className={clsx('c-fs-body2 flex justify-between py-4', {
                ['border-b border-bor-neu-mut']: index !== serviceHistory.length - 1,
              })}
              key={`${item.provider}-${item.serviceDate}`}
            >
              <span>
                {formatServiceDate(item)}
                {!isEmpty(item.provider) && (
                  <>
                    <br />
                    <span className="c-fs-caption text-tex-def-tie2">{item.provider}</span>
                  </>
                )}
              </span>
              <span className="font-extrabold">{formatServiceKilometres(item)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const formatServiceDate = (record: ServiceHistoryRecord) => {
  return record.serviceDate ?? 'Date NA';
};

const formatServiceKilometres = (record: ServiceHistoryRecord) => {
  return record.kilometres ? formatNumber(record.kilometres, { append: 'km' }) : 'Odometer NA';
};
